import { useContext } from "@nuxtjs/composition-api";
import { useAdminUserSwitcher } from "~/components/_refactored/admin";
import TRACK_CONTACT_CLICK from "~/graphql/mutations/trackPersonTenderContactClick.graphql";

export const CONTACT_CLICK_TYPE = {
  EMAIL: "email",
  PHONE: "phone",
};

const TRACK_CLICK_ELEMENT_ID = {
  [CONTACT_CLICK_TYPE.EMAIL]: "PersonTenderEmailButton",
  [CONTACT_CLICK_TYPE.PHONE]: "PersonTenderPhoneButton",
};

export const DEVICE_TYPE = {
  MOBILE: "mobile",
  TABLET: "tablet",
  DESKTOP: "desktop",
};

// $device's flags are user-agent based and mutually exclusive (isTablet is
// !mobile && mobileOrTablet, isDesktop is !mobileOrTablet), so at most one
// matches. Returns null rather than guessing if the plugin is unavailable —
// the column is nullable and NULL reads as "unknown".
export function resolveDeviceType($device) {
  if (!$device) return null;
  if ($device.isMobile) return DEVICE_TYPE.MOBILE;
  if ($device.isTablet) return DEVICE_TYPE.TABLET;
  if ($device.isDesktop) return DEVICE_TYPE.DESKTOP;
  return null;
}

/**
 * Records that a szaki clicked the customer's email or phone button on a
 * PersonTender card, to our own DB (for SQL reporting) plus PostHog and GTM.
 *
 * Fire-and-forget on every leg: the caller must stay synchronous so the
 * native mailto:/tel: navigation is not delayed, and no failure may ever
 * become visible to the szaki — no toast, no alert, no error page.
 *
 * Nothing is recorded at all while an admin is impersonating a szaki, so admin
 * activity stays out of that szaki's numbers in every system.
 */
export function useTrackPersonTenderContactClick() {
  const { $mutate, $trackers, $track, $device } = useContext();
  const adminUserSwitcher = useAdminUserSwitcher();

  function trackPersonTenderContactClick({ personTenderId, clickType }) {
    if (!process.client) return;

    // The API cannot tell an impersonating admin from the szaki — "login as user"
    // mints a token carrying only the target's id, so context.user IS the szaki.
    // Skipping here is the only place admin clicks can be kept out of the stats.
    if (adminUserSwitcher.switchedToMate) return;

    // Not awaited. skipErrorPageOnAuthError is what stops an expired JWT
    // (UNAUTHENTICATED/FORBIDDEN) from routing the szaki to the error page —
    // showErrorPage: false alone does not cover the auth codes.
    $mutate(
      TRACK_CONTACT_CLICK,
      { personTenderId, clickType, deviceType: resolveDeviceType($device) },
      {
        showErrorPage: false,
        showErrorToast: false,
        skipErrorPageOnAuthError: true,
      }
    ).catch(() => {});

    $trackers?.trackClick(TRACK_CLICK_ELEMENT_ID[clickType], {
      personTenderId,
    });
    $track?.analyticsEvent({
      event: "person_tender_kapcsolat_kattintas",
      category: "person_tender",
      action: "kapcsolat-kattintas",
      label: clickType,
    });
  }

  return {
    trackPersonTenderContactClick,
  };
}
